/**
 * A note as it was typed, with every web address in it turned into a link (SSD-7441). The words wrap as usual; an
 * address is one token that cannot wrap, so it shows its start and ends in an ellipsis where the cell runs out, and
 * the whole address stays on the link and in its tooltip. A long address used to break letter by letter down a
 * narrow column.
 */
const URL_IN_TEXT = /(https?:\/\/[^\s<>"]+)/i;
/** Punctuation that ends a sentence rather than an address. */
const SENTENCE_END = /[.,;:!?'"]/;
const OPENER: Readonly<Record<string, string>> = { ')': '(', ']': '[', '}': '{' };

const count = (text: string, char: string): number => text.split(char).length - 1;

/**
 * Splits what the pattern matched into the address and the punctuation typed after it. A closing bracket stays with
 * the address when the address opened it ("…/wiki/Foo_(bar)"), and leaves it when it closes the sentence's own
 * bracket ("(see https://example.com/a).").
 */
export const splitTrailing = (match: string): [url: string, tail: string] => {
  let url = match;
  while (url.length > 0) {
    const last = url[url.length - 1]!;
    const opener = OPENER[last];
    const belongsToSentence =
      SENTENCE_END.test(last) || (opener !== undefined && count(url, last) > count(url, opener));
    if (!belongsToSentence) {
      break;
    }
    url = url.slice(0, -1);
  }
  return [url, match.slice(url.length)];
};

/** An address as a reader names it: no scheme, no leading www. */
export const shortUrl = (url: string): string => url.replace(/^https?:\/\//i, '').replace(/^www\./i, '');

export const LinkedText = ({ text }: { text: string }) => (
  <>
    {text.split(URL_IN_TEXT).map((part, index) => {
      // split() with one capturing group puts the matches at the odd indexes.
      if (index % 2 === 0) {
        return part;
      }
      const [url, tail] = splitTrailing(part);
      return (
        <span key={index}>
          <a className="ff-url" href={url} target="_blank" rel="noreferrer" title={url}>
            {shortUrl(url)}
          </a>
          {tail}
        </span>
      );
    })}
  </>
);
